// Normalizzazione e validazione delle opzioni. Specchio di
// `CloseYourItOptions`/`Configuration` (dart/gem): i tre campi obbligatori
// mancanti disabilitano l'SDK (no-op) invece di sollevare.

import { normalizeLogLevel } from './logLevel'
import { detectRelease } from './runtime'
import type {
  BeforeSend,
  CloseYourItOptions,
  LogLevel,
  OnDiagnostic,
  TracePropagationTarget,
} from './types'

/// Soglia di default (ms) per una richiesta HTTP esterna lenta (breadcrumb
/// `slow_external_http` + verdetto performance). Parità ruby/dart.
export const DEFAULT_SLOW_EXTERNAL_THRESHOLD_MS = 1000

export interface ResolvedOptions {
  endpointUrl: string
  token: string
  publicKey: string
  projectId: string
  environment: string
  release: string | undefined
  sampleRate: number
  beforeSend: BeforeSend | undefined
  onDiagnostic: OnDiagnostic | undefined
  debug: boolean
  autoInstall: boolean
  breadcrumbs: boolean
  autoBreadcrumbs: {
    click: boolean
    navigation: boolean
    console: boolean
    http: boolean
  }
  maxBreadcrumbs: number
  sendPii: boolean
  sendServerName: boolean
  maxQueue: number
  excludedExceptions: string[]
  captureRequest: boolean
  trackPageviews: boolean
  replay: boolean
  replayMaskAllInputs: boolean
  replayBlockSelector: string | undefined
  replayMaskTextSelector: string | undefined
  replayFlushIntervalMs: number
  logsEnabled: boolean
  logsSampleRate: number
  logsBatchSize: number
  logsFlushIntervalMs: number
  logsMinLevel: LogLevel
  slowMethodThresholdMs: number
  captureMethodArguments: boolean
  detectPerformanceIssues: boolean
  captureExternalHttp: boolean
  slowExternalThresholdMs: number
  repeatedHttpThreshold: number
  repeatedHttpWindowMs: number
  detectJank: boolean
  jankThresholdMs: number
  trackWebVitals: boolean
  webVitalsSampleRate: number
  captureResourceErrors: boolean
  tracePropagationTargets: TracePropagationTarget[]
  enabled: boolean
}

export function resolveOptions(options: CloseYourItOptions): ResolvedOptions {
  return {
    endpointUrl: (options.endpointUrl ?? '').trim().replace(/\/+$/, ''),
    token: (options.token ?? '').trim(),
    publicKey: (options.publicKey ?? '').trim(),
    projectId: (options.projectId ?? '').trim(),
    environment: options.environment ?? 'production',
    // release esplicita dal consumer, altrimenti auto-detect Node-side (tag-first,
    // parità con la gem Ruby); undefined nel browser (vedi `detectRelease`).
    release: options.release ?? detectRelease(),
    sampleRate: clampRate(options.sampleRate),
    beforeSend: options.beforeSend,
    onDiagnostic: options.onDiagnostic,
    debug: options.debug ?? false,
    autoInstall: options.autoInstall ?? true,
    breadcrumbs: options.breadcrumbs ?? true,
    autoBreadcrumbs: {
      click: options.autoBreadcrumbs?.click ?? true,
      navigation: options.autoBreadcrumbs?.navigation ?? true,
      console: options.autoBreadcrumbs?.console ?? true,
      http: options.autoBreadcrumbs?.http ?? true,
    },
    maxBreadcrumbs: options.maxBreadcrumbs ?? 50,
    sendPii: options.sendPii ?? false,
    sendServerName: options.sendServerName ?? true,
    maxQueue: positiveOr(options.maxQueue, 30),
    excludedExceptions: options.excludedExceptions ?? [],
    captureRequest: options.captureRequest ?? true,
    trackPageviews: options.trackPageviews ?? false,
    replay: options.replay ?? false,
    replayMaskAllInputs: options.replayMaskAllInputs ?? true,
    replayBlockSelector: options.replayBlockSelector,
    // CYJS-15 · In un gestionale il grosso dei dati sensibili è testo che la pagina
    // mostra (nomi, pratiche, importi), non solo quello digitato nei campi: il
    // default deve coprirlo. `*` è il selettore che in rrweb fa matchare ogni
    // elemento, quindi maschera tutti i nodi di testo. Chi vuole vedere in chiaro
    // una parte della pagina passa un selettore più stretto: l'opt-out è per
    // costruzione, e va dichiarato.
    replayMaskTextSelector: options.replayMaskTextSelector ?? '*',
    replayFlushIntervalMs: positiveOr(options.replayFlushIntervalMs, 5000),
    logsEnabled: options.logsEnabled ?? true,
    logsSampleRate: clampRate(options.logsSampleRate),
    logsBatchSize: positiveOr(options.logsBatchSize, 50),
    logsFlushIntervalMs: options.logsFlushIntervalMs ?? 5000,
    logsMinLevel: normalizeLogLevel(options.logsMinLevel ?? 'info'),
    slowMethodThresholdMs: positiveOr(options.slowMethodThresholdMs, 200),
    captureMethodArguments: options.captureMethodArguments ?? false,
    detectPerformanceIssues: options.detectPerformanceIssues ?? false,
    captureExternalHttp: options.captureExternalHttp ?? true,
    slowExternalThresholdMs: clampThreshold(options.slowExternalThresholdMs),
    repeatedHttpThreshold: positiveOr(options.repeatedHttpThreshold, 5),
    repeatedHttpWindowMs: positiveOr(options.repeatedHttpWindowMs, 5000),
    detectJank: options.detectJank ?? true,
    jankThresholdMs: positiveOr(options.jankThresholdMs, 100),
    trackWebVitals: options.trackWebVitals ?? false,
    webVitalsSampleRate: clampRate(options.webVitalsSampleRate),
    captureResourceErrors: options.captureResourceErrors ?? false,
    tracePropagationTargets: options.tracePropagationTargets ?? [],
    enabled: options.enabled ?? true,
  }
}

function clampRate(rate: number | undefined): number {
  if (rate === undefined || Number.isNaN(rate)) return 1
  if (rate < 0) return 0
  if (rate > 1) return 1
  return rate
}

// Valore > 0, altrimenti default (undefined/NaN/zero/negativi inclusi).
function positiveOr(value: number | undefined, fallback: number): number {
  if (value === undefined || Number.isNaN(value) || value <= 0) return fallback
  return value
}

// Soglia in ms: `0` è valido (disattiva la rilevazione); undefined/NaN/negativi
// ricadono sul default.
function clampThreshold(value: number | undefined): number {
  if (value === undefined || Number.isNaN(value) || value < 0) {
    return DEFAULT_SLOW_EXTERNAL_THRESHOLD_MS
  }
  return value
}

/// Confronto strutturale di due set di opzioni **risolte**, per l'idempotenza di
/// `init` (CYJS-8): equivalenti ⇒ una nuova `init` non riconfigura l'SDK. Il
/// confronto è ricorsivo su oggetti annidati e array; le funzioni (`beforeSend`)
/// sono confrontate per reference — una nuova closure conta come cambiamento, scelta
/// prudente per non ignorare mai un intento di riconfigurazione.
export function resolvedOptionsEqual(a: ResolvedOptions, b: ResolvedOptions): boolean {
  return deepEqual(a, b)
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false
  }
  // Le RegExp (in `tracePropagationTargets`) hanno chiavi enumerabili vuote: senza
  // questo ramo due pattern diversi risulterebbero equivalenti. Confronto per
  // sorgente e flag così un cambio di allowlist riconfigura davvero l'SDK.
  if (a instanceof RegExp || b instanceof RegExp) {
    return (
      a instanceof RegExp &&
      b instanceof RegExp &&
      a.source === b.source &&
      a.flags === b.flags
    )
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, index) => deepEqual(item, b[index]))
  }
  const aObj = a as Record<string, unknown>
  const bObj = b as Record<string, unknown>
  const aKeys = Object.keys(aObj)
  const bKeys = Object.keys(bObj)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(bObj, key) &&
      deepEqual(aObj[key], bObj[key]),
  )
}

/// Prefisso del bearer **segreto** (`cyi_…`, server-only). La DSN public key è
/// invece esadecimale senza prefisso (parità con la generazione backend).
export const SECRET_TOKEN_PREFIX = 'cyi_'

/// `true` se [value] è un bearer segreto `cyi_` (mai da esporre nel browser).
export function isSecretToken(value: string): boolean {
  return value.startsWith(SECRET_TOKEN_PREFIX)
}

/// Credenziale con cui il transport autentica l'ingest.
/// - `bearer`: `Authorization: Bearer <secret>` — solo lato server.
/// - `dsn`: DSN public key via `?sentry_key=` — browser-safe (nessun segreto).
export type TransportAuth =
  { kind: 'bearer'; secret: string } | { kind: 'dsn'; publicKey: string }

/// Sceglie la credenziale d'invio in base all'ambiente. Nel browser il bearer
/// segreto NON è mai eleggibile (finirebbe nel bundle/Network): serve la
/// `publicKey`. Lato server il bearer è la scelta primaria, con fallback alla
/// public key se è l'unica fornita. `null` = nessuna credenziale utilizzabile.
export function resolveTransportAuth(
  options: ResolvedOptions,
  browser: boolean,
): TransportAuth | null {
  if (browser) {
    return options.publicKey ? { kind: 'dsn', publicKey: options.publicKey } : null
  }
  if (options.token) return { kind: 'bearer', secret: options.token }
  if (options.publicKey) return { kind: 'dsn', publicKey: options.publicKey }
  return null
}

/// Motivo per cui l'SDK è disabilitato, o `null` se attivo. Dipende
/// dall'ambiente: nel browser serve la DSN public key (il bearer è server-only).
export function disabledReason(
  options: ResolvedOptions,
  browser: boolean,
): string | null {
  if (!options.enabled) return 'disabilitato da configurazione (enabled: false)'
  if (!options.endpointUrl) return 'endpointUrl mancante'
  if (!options.projectId) return 'projectId mancante'
  if (resolveTransportAuth(options, browser) === null) {
    return browser
      ? 'publicKey mancante (nel browser il bearer segreto cyi_ è server-only: usa la DSN public key)'
      : 'token mancante'
  }
  if (
    options.environment === 'production' &&
    options.endpointUrl.startsWith('http://')
  ) {
    return 'endpoint http:// non ammesso in produzione'
  }
  return null
}

export function isEnabled(options: ResolvedOptions, browser: boolean): boolean {
  return disabledReason(options, browser) === null
}

/// URI di ingest per gli eventi errore/messaggio (auth bearer, path v1).
export function eventsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/events`
}

/// URI drop-in Sentry per gli eventi errore/messaggio quando si autentica via
/// DSN public key: il backend espone `POST /api/:project_id/store` (evento JSON
/// singolo), auth `?sentry_key=`. È il canale browser-safe per gli errori.
export function storeUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/${options.projectId}/store`
}

/// URI di ingest per i log (batch).
export function logsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/logs`
}

/// URI di ingest per le metriche (slow_method, performance_issue).
export function metricsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/metrics`
}

/// URI di ingest per le pageview (web analytics).
export function pageviewsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/pageviews`
}

/// URI di ingest per i chunk di session replay.
export function replayUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/replays`
}

/// URI di ingest per le misure Web Vitals (LCP/CLS/INP/TTFB/FCP dai visitatori
/// veri). Endpoint suo, non un ramo dei pageview: forma, tetto di batch e
/// throttle sono diversi (CYJS-12, CYJS-28).
export function webVitalsUri(options: ResolvedOptions): string {
  return `${options.endpointUrl}/api/v1/projects/${options.projectId}/web_vitals`
}

/// Host dell'endpoint (per escludere la telemetria dai breadcrumb HTTP).
export function endpointHost(options: ResolvedOptions): string | undefined {
  try {
    return new URL(options.endpointUrl).host
  } catch {
    return undefined
  }
}
